<template>
  <div>
    <div :class="containerClass">
      <page-header
        :title="title"
        :container-class="null"
        :info-text="'Compare courses side-by-side. Use filters below to narrow down, and export per-course rosters.'"
        :show-back="true"
        class="mb-32pt"
      />
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <!-- ============== Filters ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="filter-row">
              <div class="filter-item filter-item--lg">
                <label>Date Range</label>
                <date-picker
                  v-model="filters.dateRange"
                  format="MMM DD, YYYY"
                  valueType="YYYY-MM-DD"
                  range
                  class="w-100"
                  placeholder="Start - End"
                />
              </div>
              <div class="filter-item">
                <label>Program</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.program"
                  :reduce="o => o.id"
                  :options="allPrograms"
                  placeholder="All Programs"
                  :clearable="true"
                />
              </div>
              <div class="filter-item">
                <label>Cohort/Tag</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.tag"
                  :reduce="o => o.id"
                  :options="allTags"
                  placeholder="All Tags"
                  :clearable="true"
                />
              </div>
              <div class="filter-item filter-item--lg">
                <label>Search</label>
                <input
                  type="text"
                  v-model="filters.search"
                  @input="debouncedFetch"
                  class="form-control"
                  placeholder="Course title..."
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ============== Leaderboard Table ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Course Performance Leaderboard</h5>
                <p class="section-sub">
                  {{ rows.length ? `Showing ${rows.length} course${rows.length === 1 ? '' : 's'}.` : '' }}
                </p>
              </div>
              <!-- <b-btn
                variant="primary"
                size="sm"
                :disabled="isExportLoading"
                @click.prevent="exportCsv"
              >
                Export CSV
                <i v-if="isExportLoading" class="fas fa-circle-notch fa-spin ml-1"></i>
                <span v-else class="material-icons icon-16pt ml-1">file_download</span>
              </b-btn> -->
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading leaderboard...</strong>
            </div>
            <div v-else-if="rows.length" class="table-responsive mt-2">
              <table class="leaderboard-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th class="text-right">Enrolled</th>
                    <th class="text-right">In Progress</th>
                    <th class="text-right">Completed</th>
                    <th class="text-right">Dropped</th>
                    <th class="text-right">Completion %</th>
                    <th class="text-right">Roster</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.course_id">
                    <td>
                      <a href="#" class="course-link" @click.prevent="goToCourseAnalytics(row.course_id)">
                        {{ row.course_title }}
                      </a>
                    </td>
                    <td class="text-right">{{ row.enrolled_count }}</td>
                    <td class="text-right">{{ row.in_progress_count }}</td>
                    <td class="text-right">{{ row.completed_count }}</td>
                    <td class="text-right">{{ row.dropped_count }}</td>
                    <td class="text-right">
                      <span class="metric-pill metric-pill--green">{{ formatPct(row.completion_rate) }}%</span>
                    </td>
                    <td class="text-right">
                      <b-btn
                        variant="outline-primary"
                        size="sm"
                        class="d-inline-flex align-items-center roster-btn"
                        :disabled="rosterExportingCourseId === row.course_id"
                        @click.prevent="exportRosterFor(row.course_id)"
                        title="Export per-student roster CSV"
                      >
                        <i
                          v-if="rosterExportingCourseId === row.course_id"
                          class="fas fa-circle-notch fa-spin mr-1"
                        ></i>
                        <span v-else class="material-icons icon-16pt mr-1">file_download</span>
                        Roster
                      </b-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-muted py-4">No courses match the current filters.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { debounce } from 'lodash';
import DatePicker from 'vue2-datepicker';
import vSelect from 'vue-select';
import moment from 'moment';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { formatToAPIDate } from '../../../common/utils';

const defaultRange = () => [
  formatToAPIDate(moment().startOf('month')),
  formatToAPIDate(moment().endOf('month')),
];

export default {
  components: { DatePicker, vSelect, PageHeader },
  extends: Page,
  data() {
    return {
      title: 'Course Leaderboard',

      allPrograms: [],
      allTags: [],

      filters: {
        dateRange: defaultRange(),
        program: null,
        tag: null,
        search: '',
      },

      rows: [],
      isLoading: false,
      isExportLoading: false,
      rosterExportingCourseId: null,
    };
  },
  methods: {
    ...mapActions('lmsAnalytics', [
      'getCourseLeaderboard',
      'exportCourseLeaderboard',
      'exportCourseRoster',
    ]),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('attendance', ['getTags']),
    ...mapActions('fileDownload', ['downloadFile']),

    formatPct(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toFixed(1);
    },
    dateParams() {
      const r = this.filters.dateRange || [];
      return { date_from: r[0] || null, date_to: r[1] || null };
    },
    buildLeaderboardParams({ includeSearch = true } = {}) {
      const params = { ...this.dateParams() };
      if (this.filters.program) params.program_id = this.filters.program;
      if (this.filters.tag) params.tag_id = this.filters.tag;
      const search = (this.filters.search || '').trim();
      if (includeSearch && search) params.search = search;
      return params;
    },
    unwrapListEnvelope(res) {
      const payload = res && res.data ? res.data : res;
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },
    validateRange(maxMonths = 24) {
      const r = this.filters.dateRange;
      if (!r || !r[0] || !r[1]) return true;
      const diff = moment(r[1]).diff(moment(r[0]), 'months', true);
      if (diff > maxMonths) {
        this.makeToast({ variant: 'danger', msg: `Date range must be ${maxMonths} months or less.` });
        return false;
      }
      return true;
    },
    unwrapUrl(res) {
      return (res && res.data && res.data.url) || (res && res.url) || null;
    },
    async runDownload(url) {
      try {
        await this.downloadFile({ fileUrl: url });
        this.makeToast({ variant: 'success', msg: 'File downloaded.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to download file.' });
      }
    },

    seedFromQuery() {
      const q = this.$route.query || {};
      if (q.date_from && q.date_to) {
        this.filters.dateRange = [q.date_from, q.date_to];
      }
      if (q.program_id) this.filters.program = q.program_id;
      if (q.tag_id) this.filters.tag = q.tag_id;
      // course_id from main page ignored here — leaderboard compares courses.
    },

    async fetchLookups() {
      try {
        const [progRes, tagsRes] = await Promise.all([
          this.getAllPrograms(),
          this.getTags({ limit: 200 }).catch(() => null),
        ]);
        this.allPrograms = (progRes && progRes.data) || [];
        const tagList = this.unwrapListEnvelope(tagsRes);
        this.allTags = (Array.isArray(tagList) ? tagList : []).map(t => ({ id: t.id, title: t.title || t.name }));
      } catch (e) { /* silent */ }
    },

    async fetchLeaderboard() {
      if (!this.validateRange(24)) return;
      this.isLoading = true;
      try {
        const res = await this.getCourseLeaderboard(this.buildLeaderboardParams());
        this.rows = this.unwrapListEnvelope(res);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load course leaderboard.' });
      }
      this.isLoading = false;
    },

    async exportCsv() {
      this.isExportLoading = true;
      try {
        const res = await this.exportCourseLeaderboard(this.buildLeaderboardParams());
        const url = this.unwrapUrl(res);
        if (url) await this.runDownload(url);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to export leaderboard.' });
      }
      this.isExportLoading = false;
    },

    async exportRosterFor(courseId) {
      if (!this.validateRange(6)) return;
      this.rosterExportingCourseId = courseId;
      try {
        const res = await this.exportCourseRoster({
          ...this.buildLeaderboardParams({ includeSearch: false }),
          course_id: courseId,
        });
        const url = this.unwrapUrl(res);
        if (url) await this.runDownload(url);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to export course roster.' });
      }
      this.rosterExportingCourseId = null;
    },

    goToCourseAnalytics(courseId) {
      this.$router.push({ name: 'course-analytics', params: { id: courseId } });
    },
  },
  watch: {
    'filters.dateRange': 'fetchLeaderboard',
    'filters.program': 'fetchLeaderboard',
    'filters.tag': 'fetchLeaderboard',
  },
  created() {
    this.debouncedFetch = debounce(this.fetchLeaderboard, 400);
  },
  async mounted() {
    this.seedFromQuery();
    await this.fetchLookups();
    this.fetchLeaderboard();
  },
};
</script>

<style scoped lang="scss">
.analytics-card {
  border: 1px solid #e7ecf5;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
  background: #fff;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
  flex-wrap: wrap;
}
.section-title {
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
}
.section-sub { margin: 0; color: #64748b; font-size: 0.85rem; }

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.filter-item {
  flex: 1 1 180px;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
  }
}
.filter-item--lg { flex: 1 1 240px; min-width: 220px; }

.leaderboard-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  thead th {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.92rem;
  }
  tbody tr:hover { background: #f8fafc; }
}
.course-link { color: #4f46e5; text-decoration: none; font-weight: 600; }
.course-link:hover { text-decoration: underline; }

.metric-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  &--green { background: rgba(16, 185, 129, 0.12); color: #047857; }
}
</style>
